import {Template} from "../../../components/template/template.jsx";
import {Card} from "../../../components/card/card.jsx";
import {useEffect, useState} from "react";
import {useMutation, useQuery} from "@tanstack/react-query";
import SentryCloudService from "../../../services/web/sentry-cloud-service.js";
import {toast} from "react-toastify";
import ProjectUtils from "../../../utils/project-utils.js";
import InputSwitch from "../../../components/input/input-switch.jsx";
import Alert from "../../../components/alert/alert.jsx";
import {FaExclamationTriangle} from "react-icons/fa";
import destroyUtils from "../../../utils/destroy-utils.js";

export default function SentryCloud() {
    const [cloneStatus, setCloneStatus] = useState("DISABLE");
    const [shareAddress, setShareAddress] = useState(false);

    const { data, isLoading, error } = useQuery({
        queryFn: () => SentryCloudService.getCloneConfig(),
        queryKey: ["sentry-cloud"],
    });

    useEffect(() => {
        if (!data) return;

        if (data.cloneStatus) {
            setCloneStatus(data.cloneStatus === "SHARE_ALL" ? "RESTRICT" : data.cloneStatus);
        }

        if (data.shareAddress !== undefined) {
            setShareAddress(data.shareAddress);
        }
    }, [data]);

    const saveMutation = useMutation({
        mutationFn: (data) => SentryCloudService.saveCloneConfig(data),
        onSuccess: () => {
            toast.success("Salvo com sucesso.");
        },
        onError: (error) => {
            console.error("Erro ao salvar.", error.response?.data || error);
            const message = error.response?.data.message || "Não foi possível salvar.";
            ProjectUtils.swalErrorMessage("", message, "Ok");
        }
    });

    const updateConfig = (newConfig) => {
        saveMutation.mutate({
            cloneStatus,
            shareAddress,
            ...newConfig
        });
    }

    const handleCloneStatusChange = async (checked) => {
        if (checked) {
            setCloneStatus("RESTRICT");
            updateConfig({ cloneStatus: "RESTRICT" });
            return;
        }

        const confirmed = await destroyUtils.keywordDestroy("Ao desabilitar esta funcionalidade, as passagens da sua corporação deixarão de ser utilizadas na verificação de clones.", "desativar verificação de clones");

        if (!confirmed) return;

        setCloneStatus("DISABLE");
        updateConfig({ cloneStatus: "DISABLE" });
    }

    const isIoError = error?.response?.data?.message === "io.error";

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Configurações de clone</Template.Title>
            </Template.Header>
            <Template.Body className="space-y-2">
                {isIoError ? (
                    <Alert target="danger">
                        SentryCloud offline.
                    </Alert>
                ) : (
                    <>
                        <Card>
                            <Card.Body className="space-y-6 m-4">
                                <article className="bg-[#FEF8E7] text-[#73510d] px-3 pt-3 pb-5 rounded-md border border-[#E7A83A]">
                                    <div className="flex gap-4">
                                        <FaExclamationTriangle className="text-[#E7A83A]" size={35} />
                                        <div className="space-y-0.5">
                                            <h1 className="text-[#E7A83A] text-[19px]! font-bold">Verificação de clones</h1>
                                            <p className="text-[15px] font-semibold!">A verificação de clones é realizada a partir do cruzamento de passagens veiculares entre as corporações. Ao habilitar essa funcionalidade, as passagens da sua corporação serão compartilhadas e utilizadas nesse processo.</p>
                                        </div>
                                    </div>
                                </article>

                                <div className="border-b border-gray-200" />

                                <section className="flex flex-col">
                                    <h4>Habilitar recursos</h4>
                                    <div className="space-y-4 mt-3">
                                        <InputSwitch
                                            label="Verificação de clone"
                                            checked={cloneStatus === "RESTRICT"}
                                            onChange={handleCloneStatusChange}
                                            classNameLabel="font-bold"
                                        />
                                        <InputSwitch
                                            label="Exibir o endereço das câmeras OCR da sua corporação nas notificações"
                                            checked={shareAddress}
                                            onChange={(checked) => {
                                                setShareAddress(checked);
                                                updateConfig({ shareAddress: checked });
                                            }}
                                        />
                                    </div>
                                </section>

                                <div className="border-b border-gray-200" />

                                <section className="space-y-3">
                                    <h4>Restrições</h4>
                                    <span>As corporações listadas abaixo estão <b>bloqueadas</b> e não participam da verificação de clones com a sua corporação. O bloqueio é realizado pelo Suporte Técnico mediante solicitação do administrador.</span>
                                    <div className="bg-[#FAFAFA] border border-[#E5E7EB] rounded-lg p-2 mt-3 w-fit min-w-[320px]">
                                        <ul className="list-none pl-2 space-y-1">
                                            {data?.deniedCities?.length > 0 ? (
                                                data?.deniedCities.map((item) => (
                                                    <li key={item.idSentry} className="font-semibold">
                                                        - {item.name}
                                                    </li>
                                                ))
                                            ) : (
                                                <li>Nenhuma corporação bloqueada.</li>
                                            )}
                                        </ul>
                                    </div>
                                </section>
                            </Card.Body>
                        </Card>
                    </>
                )}
            </Template.Body>
        </Template>
    )
}
